// (c) chaehongee. Confidential and proprietary.

import { useEffect } from 'react'
import { useAppDispatch } from '../../../store/hooks/hooks'
import { setIsProcessing, clearAllPending } from '../../../store/slices/chatSlice'
import { getSessionProcessingStatus } from '../../../services/agent'

/**
 * On mount (page load / refresh), checks whether the current session has an
 * active background producer on the server.  If so, sets `isProcessing: true`
 * and polls every 2 s until the producer finishes, then refreshes the session
 * to pick up any new nodes/commits.
 */
export function useSessionRecovery(
    sessionId: string | null,
    refreshSession: () => Promise<void>
) {
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (!sessionId) return

        let cancelled = false

        const check = async () => {
            try {
                const { is_processing } = await getSessionProcessingStatus(sessionId)
                if (cancelled) return

                if (is_processing) {
                    dispatch(setIsProcessing({ sessionId, isProcessing: true }))
                    setTimeout(check, 2000)
                } else {
                    // Producer may have just finished — refresh to pick up results.
                    dispatch(setIsProcessing({ sessionId, isProcessing: false }))
                    dispatch(clearAllPending({ sessionId }))
                    await refreshSession()
                }
            } catch {
                // Non-critical — don't block the UI.
            }
        }

        check()

        return () => {
            cancelled = true
        }
    }, [sessionId, dispatch, refreshSession])
}
